import { useState, useMemo, type CSSProperties } from 'react';
import { useT } from '../i18n';

export interface AIPanelDateRange {
  dateFrom?: string;
  dateTo?: string;
}

interface AIPanelProps {
  title: string;
  onGenerate: (range?: AIPanelDateRange) => Promise<string>;
  buttonLabel?: string;
  /** Show inline from/to date inputs; their values are passed to onGenerate. */
  withDateRange?: boolean;
  /** Small muted note under the title (e.g. which period feeds the generation). */
  hint?: string;
}

/** Lightweight markdown → HTML for AI output (bold, italic, headers, lists, paragraphs) */
function renderMarkdown(md: string): string {
  return md
    // Headers: ### → h4, ## → h3, # → h2
    .replace(/^### (.+)$/gm, '<h4>$1</h4>')
    .replace(/^## (.+)$/gm, '<h3>$1</h3>')
    .replace(/^# (.+)$/gm, '<h2>$1</h2>')
    // Bold + italic
    .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
    // Bold
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    // Italic
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    // Unordered lists (- item or * item)
    .replace(/^[-*] (.+)$/gm, '<li>$1</li>')
    // Wrap consecutive <li> in <ul>
    .replace(/((?:<li>.*<\/li>\n?)+)/g, '<ul>$1</ul>')
    // Numbered lists (1. item)
    .replace(/^\d+\. (.+)$/gm, '<li>$1</li>')
    .replace(/((?:<li>.*<\/li>\n?)+)/g, (match) => {
      // If already wrapped in <ul>, skip
      if (match.startsWith('<ul>')) return match;
      return `<ol>${match}</ol>`;
    })
    // Line breaks → paragraphs (double newline)
    .replace(/\n{2,}/g, '</p><p>')
    // Single newlines inside text → <br>
    .replace(/\n/g, '<br>')
    // Wrap in <p>
    .replace(/^/, '<p>')
    .replace(/$/, '</p>')
    // Clean up empty paragraphs
    .replace(/<p>\s*<\/p>/g, '')
    // Clean up <p> around block elements
    .replace(/<p>(<h[234]>)/g, '$1')
    .replace(/(<\/h[234]>)<\/p>/g, '$1')
    .replace(/<p>(<ul>)/g, '$1')
    .replace(/(<\/ul>)<\/p>/g, '$1')
    .replace(/<p>(<ol>)/g, '$1')
    .replace(/(<\/ol>)<\/p>/g, '$1');
}

const dateInputStyle: CSSProperties = {
  fontSize: 'var(--t-12)', padding: '3px 6px',
  borderRadius: 'var(--r1)', border: '1px solid var(--line)',
  color: 'var(--ink-1)', background: 'var(--bg)',
};

export function AIPanel({ title, onGenerate, buttonLabel, withDateRange, hint }: AIPanelProps) {
  const { t } = useT();
  const [result, setResult] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const html = useMemo(() => result ? renderMarkdown(result) : null, [result]);

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    try {
      const range = withDateRange
        ? { dateFrom: dateFrom || undefined, dateTo: dateTo || undefined }
        : undefined;
      const text = await onGenerate(range);
      setResult(text);
    } catch (err: any) {
      setError(err.message || t('ai.generationFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ai-panel" style={{
      border: '1px solid var(--line)',
      borderRadius: 'var(--r1)',
      padding: 'var(--s12) var(--s16)',
      backgroundColor: 'var(--surface)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--s8)', flexWrap: 'wrap', marginBottom: html || error || hint ? 'var(--s8)' : 0 }}>
        <h3 style={{ fontSize: 'var(--t-13)', fontWeight: 600, color: 'var(--ink-1)', margin: 0 }}>{title}</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s8)', flexWrap: 'wrap' }}>
          {withDateRange && (
            <>
              <label style={{ fontSize: 'var(--t-11)', color: 'var(--ink-3)' }}>{t('ai.from')}</label>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={dateInputStyle} />
              <label style={{ fontSize: 'var(--t-11)', color: 'var(--ink-3)' }}>{t('ai.to')}</label>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={dateInputStyle} />
            </>
          )}
          <button
            className="btn btn-accent btn-sm"
            onClick={handleGenerate}
            disabled={loading}
          >
            {loading ? '...' : (buttonLabel || t('ai.generate'))}
          </button>
        </div>
      </div>

      {hint && (
        <div style={{ fontSize: 'var(--t-11)', color: 'var(--ink-3)', marginBottom: html || error ? 'var(--s8)' : 0 }}>
          {hint}
        </div>
      )}

      {error && (
        <div style={{ color: 'var(--err)', fontSize: 'var(--t-12)', marginTop: 'var(--s4)' }}>
          {error}
        </div>
      )}

      {html && (
        <div
          className="ai-result"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      )}
    </div>
  );
}
